import React from "react";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ru";

/**
 * Возраст товара на Kaspi — компактный бейдж под артикулом.
 * Данные приходят из таблицы sku_creation_time (поле created_time).
 */

const PALETTES = {
  fresh: { bg: "#ecfdf5", border: "#a7f3d0", text: "#047857" },
  young: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
  mature: { bg: "#f5f3ff", border: "#ddd6fe", text: "#6d28d9" },
  veteran: { bg: "#fff7ed", border: "#fed7aa", text: "#c2410c" },
};

const pluralRu = (n, forms) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};

const formatAge = (months) => {
  if (months < 1) return "меньше месяца";
  if (months < 12) return `${months} ${pluralRu(months, ["месяц", "месяца", "месяцев"])}`;

  const years = Math.floor(months / 12);
  const restMonths = months % 12;
  const yearsLabel = `${years} ${pluralRu(years, ["год", "года", "лет"])}`;

  if (restMonths === 0) return yearsLabel;
  return `${yearsLabel} ${restMonths} ${pluralRu(restMonths, ["мес.", "мес.", "мес."])}`;
};

const pickPalette = (months) => {
  if (months < 3) return PALETTES.fresh;
  if (months < 12) return PALETTES.young;
  if (months < 36) return PALETTES.mature;
  return PALETTES.veteran;
};

export const ProductAge = ({ createdTime }) => {
  if (!createdTime) return null;

  const created = dayjs(createdTime).locale("ru");
  if (!created.isValid()) return null;

  const months = Math.max(0, dayjs().diff(created, "month"));
  const palette = pickPalette(months);
  const isFresh = months < 3;

  return (
    <Tooltip
      title={
        <span>
          Товар появился на Kaspi
          <br />
          {created.format("D MMMM YYYY, HH:mm")}
          <br />
          На площадке уже {formatAge(months)}
        </span>
      }
    >
      <span
        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-px-2 tw-py-[2px] tw-mt-1 tw-cursor-default tw-whitespace-nowrap tw-leading-none tw-text-[10px] md:tw-text-[11px] tw-font-medium"
        style={{
          backgroundColor: palette.bg,
          borderColor: palette.border,
          color: palette.text,
        }}
      >
        {isFresh ? (
          <span aria-hidden="true">✨</span>
        ) : (
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <rect
              x="3"
              y="5"
              width="18"
              height="16"
              rx="3"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M3 10h18M8 3v4M16 3v4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}
        <span>с {created.format("MMM YYYY")}</span>
        <span style={{ opacity: 0.55 }}>·</span>
        <span>{formatAge(months)}</span>
      </span>
    </Tooltip>
  );
};

export default ProductAge;
